/**
 * The allocate dialog's destinations (TMMAPP-0008): the envelopes of every virtual account the caller
 * may allocate into — the ones they own and, since TMMAPP-0110 (MM-D-120), the ones they manage
 * (a Manager contributes from their own accounts, in the owner's currency). Archived envelopes are excluded; order is virtual account name → sortOrder →
 * name. Since TMMAPI-0145 the virtual accounts themselves are destinations too (into their
 * Unassigned, MM-D-113), so the context also carries each owned virtual account with its served
 * balances (`balanceMinor`, `unassignedMinor`), read from the shell's shared `GET /v1/virtual-accounts`
 * rows, never derived.
 * Categories belong to the virtual account (MM-D-124): `Envelope.categories` is the virtual account's
 * list projected onto the row; a null list is filled lazily by the dialog via `categoriesApi.list`.
 */
import { createContext, useContext } from 'react';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import type { VirtualAccountListItem } from '@/api/virtualAccounts';

export type OwnedEnvelope = Envelope & { virtualAccountId: number; virtualAccountName: string };

/** An owned virtual account as a destination group: its figures are the served ones. */
export interface OwnedVirtualAccount {
  id: number;
  name: string;
  /** The owner's currency; '' when `/me` carries none. */
  currency: string;
  balanceMinor: number | null;
  unassignedMinor: number | null;
}

export type OwnedEnvelopesStatus = 'loading' | 'ready' | 'error';

export interface OwnedEnvelopesValue {
  status: OwnedEnvelopesStatus;
  /** What the failed fetch threw, while `status` is `error` (read by `ErrorState`). */
  error?: unknown;
  envelopes: OwnedEnvelope[];
  /** The caller's `mine` virtual accounts, in `/me` order, with their served balances. */
  virtualAccounts: OwnedVirtualAccount[];
  reload: () => Promise<void>;
}

export const OwnedEnvelopesContext = createContext<OwnedEnvelopesValue | null>(null);

export function sortOwnedEnvelopes(envelopes: OwnedEnvelope[]): OwnedEnvelope[] {
  return [...envelopes].sort((a, b) => {
    const byVirtualAccount = a.virtualAccountName.localeCompare(b.virtualAccountName);
    if (byVirtualAccount !== 0) return byVirtualAccount;
    const orderA = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const orderB = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

/** A destination standing: served, else (a pre-0146 row) the owner's `mine`. */
interface DestinationStanding {
  mine: boolean;
  myRole?: string;
  canManage?: boolean;
}

/**
 * The virtual accounts the caller may allocate into (MM-D-113 / 120): the owner's own, and the ones
 * they manage while the served `canManage` holds (the owner ACTIVE). Never inferred from shares.
 */
export function isAllocationDestination(virtualAccount: DestinationStanding): boolean {
  return (
    virtualAccount.mine ||
    (virtualAccount.myRole === 'MANAGER' && virtualAccount.canManage === true)
  );
}

export async function fetchOwnedEnvelopes(
  virtualAccounts: ({ id: number; name: string } & DestinationStanding)[],
): Promise<OwnedEnvelope[]> {
  const mine = virtualAccounts.filter(isAllocationDestination);
  if (mine.length === 0) return [];
  const settled = await Promise.allSettled(
    mine.map(async (virtualAccount) => {
      const envelopes = await envelopesApi.listForVirtualAccount(virtualAccount.id);
      return envelopes
        .filter((envelope) => envelope.archivedAt === null)
        .map((envelope) => ({
          ...envelope,
          virtualAccountId: virtualAccount.id,
          virtualAccountName: virtualAccount.name,
        }));
    }),
  );
  const lists = settled.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
  if (lists.length === 0 && settled.every((result) => result.status === 'rejected')) {
    const first = settled[0];
    throw first !== undefined && first.status === 'rejected'
      ? first.reason
      : new Error('owned envelopes failed');
  }
  return sortOwnedEnvelopes(lists);
}

/**
 * The owned virtual accounts as destination groups: `/me` names them; the shell's shared
 * `GET /v1/virtual-accounts` rows (when the shell has them) supply the served balances. A group
 * whose row is missing shows no balance line, never a derived one.
 */
export function ownedVirtualAccountsFrom(
  virtualAccounts: ({ id: number; name: string; currency: string } & DestinationStanding)[],
  served: readonly VirtualAccountListItem[],
): OwnedVirtualAccount[] {
  return virtualAccounts.filter(isAllocationDestination).map((virtualAccount) => {
    const row = served.find((item) => item.id === virtualAccount.id);
    return {
      id: virtualAccount.id,
      name: virtualAccount.name,
      currency: virtualAccount.currency,
      balanceMinor: row?.balanceMinor ?? null,
      unassignedMinor: row?.unassignedMinor ?? null,
    };
  });
}

export function useOwnedEnvelopes(): OwnedEnvelopesValue {
  const value = useContext(OwnedEnvelopesContext);
  if (!value) throw new Error('useOwnedEnvelopes() must be used inside <OwnedEnvelopesProvider>.');
  return value;
}
